
<template>
    <div class="user-status" v-click-outside="closeDropdown">
        <div class="selected-status" :class="{ open: isDropdownOpen }" @click="toggleDropdown">
            {{ user.status || 'Selecteer een status' }}
            <span 
                class="vl-icon vl-icon--light vl-vi" 
                :class="isDropdownOpen ? 'vl-vi-nav-up-light' : 'vl-vi-nav-down-light'"
                aria-hidden="true">
              </span>
        </div>

        <!-- Status Dropdown (conditionally visible) -->
        <transition name="fade">
            <div v-if="isDropdownOpen" class="custom-dropdown">
                <div v-for="(item, index) in statusMessages" :key="index" class="dropdown-category">
                    <!-- Category Header -->
                    <div v-if="item.isNameVisible" class="category-header" @click="toggleCategory(index)">
                        {{ item.category }}
                        
                        <span 
                            class="vl-icon vl-icon--light vl-vi" 
                            :class="isCategoryOpen(index) ? 'vl-vi-nav-up-light' : 'vl-vi-nav-down-light'">
                        </span>
                    </div>
                    
                    <!-- Substatuses (conditionally shown) -->
                    <ul v-if="isCategoryOpen(index)" class="substatus-list">
                        <li 
                            v-for="(status, subIndex) in item.statuses" 
                            :key="subIndex" 
                            @click="selectStatus(status.status)" 
                            :class="{ selected: selectedStatus === status.status }"
                        >
                            {{ status.status }}
                        </li>
                    </ul>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import clickOutside from '@/helpers/click-outside';
import StatusTimer from '@/components/StatusTimer';

const LOG_OUT = 'Niet aan het werk';

export default {
  name: 'StatusDropdown',

  components: {
    StatusTimer,
  },

  data() {
    return {
        isDropdownOpen: false, // Controls dropdown visibility
        selectedStatus: null,
        openCategories: [], // To track which categories are open
    };
  },

  computed: {
    ...mapGetters('cti', ['isConnected', 'isAlerting']),
    ...mapGetters('authentication', ['isLoggedIn']),
    ...mapState('cti', ['statusMessages', 'user']),
    ...mapGetters('metadata', ['getLabelByName']),
  },
  directives: {
    clickOutside,
  },
  methods: {
    ...mapActions('cti', {
      updateStatus: 'updateStatus',
      ctiLogOut: 'logOut',
    }),
    ...mapActions('authentication', {
      crmLogOut: 'logOut',
    }),

    /* Triggered when user confirms logout
    confirmLogOut() {
      this.$store
        .dispatch('cti/updateStatus', 'Uitgelogd')
        .then((response) => console.log('Update status success:', response))
        .catch((error) => console.error('Update status failed:', error));

      Promise.all([this.crmLogOut()]);
    },
    */

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },

    closeDropdown() {
      this.isDropdownOpen = false; // Close dropdown when clicked outside
    },

    selectStatus(status) {
      this.selectedStatus = status;
      this.isDropdownOpen = false; // Close the dropdown after selection
      this.onChangeStatus(status);
    },

    onChangeStatus(id) {
      if (id === LOG_OUT) {
        this.ctiLogOut();
        this.$store.commit('cti/UPDATE_USER', { isLoggedIn: false });
      }

      this.$store
        .dispatch('cti/updateStatus', id)
        .then((response) => 
          console.log('Update status success:', response)
        )
        .catch((error) => console.error('Update status failed:', error));

      // Close the popover
      if (this.$refs.statusPopover) {
        this.$refs.statusPopover.expanded = false;
      }
    },

    toggleCategory(index) {
      if (this.openCategories.includes(index)) {
        this.openCategories = this.openCategories.filter((i) => i !== index);
      } else {
        this.openCategories.push(index);
      }
    },
    
    isCategoryOpen(index) {
      return this.openCategories.includes(index);
    },
  },
  mounted() {
    // Initialize open categories based on the `isCollapsed` property
    this.openCategories = this.statusMessages
      .map((category, index) => (!category.isCollapsed ? index : null))
      .filter((index) => index !== null); // Only include indices of open categories
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.primary-header__right .user-status {
  position: relative;
  flex-shrink: 0;
  padding-left: 1.2rem;
}

.primary-header__right .icon-offset {
  margin-top: 10px;
}

/* Remove the custom-dropdown code after tailwindcss is correctly implemented! */
.custom-dropdown {
  position: absolute;
  z-index: 50;
  background-color: #ffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px, rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  left: 0;
  right: 0;
  width: 100%;
  max-height: 100vh;
  overflow: auto;
}

@media (min-width: 640px) {
  .custom-dropdown {
    width: auto;
    min-width: 280px;
    max-width: 28rem;
    right: 0;
    left: auto;
  }
}

.dropdown-category {
  margin-bottom: 2px;
}

.category-header {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem 0.25rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 1.5rem;
  font-weight: 600;
}

.substatus-list {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  max-height: 80vh;
  overflow-y: auto;
}

@media (min-height: 700px) {
  .substatus-list {
    padding-top: 2px;
    max-height: 400px;
  }
}

.substatus-list li {
  cursor: pointer;
  font-size: 1.5rem;
  padding: 2px 8px;
}

.substatus-list li.selected {
  font-weight: bold;
  color: #05c;
}

.selected-status {
  cursor: pointer;
  font-weight: bold;
  color: #05c;
  min-width: 0;
  max-width: 400px;
  margin-left: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.selected-status .vl-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.substatus-list li:hover {
  background-color: #05c;
  color: #fff;
  border-radius: 0.5rem;
}

/* fix to slightly reduce font size on smaller devices */
@media (max-width: 1566px) {
  .category-header {
    font-size: 1.35rem;
  }
  .substatus-list li {
    font-size: 1.3rem;
  }
}
</style>